import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import propertyViewingsService from "../services/propertyViewingsService";
import Modal from "../components/Modal";
import PropertyViewingForm from "../components/PropertyViewingFormFinal";
import LunchForm from "../components/LunchForm";
import PropertyViewingsCalendar from "../components/PropertyViewingsCalendar";
import "../assets/property-viewings.css";
import Loader from "../components/Loader";
import { toast } from "react-toastify";

const PropertyViewingsUserCalendar = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [viewings, setViewings] = useState([]);
  const [allViewingsForCalendar, setAllViewingsForCalendar] = useState([]); // All viewings for calendar (no pagination)
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [viewingStats, setViewingStats] = useState({
    all: 0,
    upcoming: 0,
    past: 0,
    completed: 0,
    uncompleted: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showLunchModal, setShowLunchModal] = useState(false);
  const [selectedViewing, setSelectedViewing] = useState(null);
  const [selectedLunch, setSelectedLunch] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [calendarViewMode, setCalendarViewMode] = useState("listWeek");
  const [modalUserData, setModalUserData] = useState(null);
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(30);
  const [totalRecords, setTotalRecords] = useState(0);

  const loadUserData = useCallback(async () => {
    try {
      console.log("🔍 [CALENDAR] Loading user data for ID:", id);
      const userData = await propertyViewingsService.getUser(id);
      console.log("✅ [CALENDAR] User data received:", userData);
      setUser(userData);
    } catch (err) {
      console.error("❌ [CALENDAR] Failed to load user data:", err);
      // Set default user data to ensure page loads
      const defaultUser = {
        id: id,
        fullName: `User ${id}`,
        email: `user${id}@example.com`,
      };
      console.log("🔧 [CALENDAR] Setting default user data:", defaultUser);
      setUser(defaultUser);
      setError("Failed to load user data - using default");
    }
  }, [id]);

  const loadViewingStats = useCallback(async () => {
    try {
      console.log("Loading viewing stats for user:", id);
      const stats = await propertyViewingsService.getUserViewingStatsForCalendar(id);
      console.log("Received viewing stats:", stats);
      setViewingStats(stats);
    } catch (err) {
      console.error("Failed to load viewing stats:", err);
      // Set default stats to ensure page loads
      setViewingStats({
        all: 0,
        upcoming: 0,
        past: 0,
        completed: 0,
        uncompleted: 0,
      });
    }
  }, [id]);

  // Load ALL viewings for calendar (no pagination)
  const loadAllViewingsForCalendar = useCallback(async () => {
    try {
      console.log(
        "🔄 [LOAD ALL VIEWINGS FOR CALENDAR] Loading ALL viewings for calendar (no pagination) for user:",
        id,
        "status:",
        selectedStatus
      );
      
      // Map frontend status to backend status (Laravel-compatible)
      let backendStatus = selectedStatus;
      if (selectedStatus === "all") {
        backendStatus = "all";
      } else if (selectedStatus === "upcoming") {
        backendStatus = "upcoming";
      } else if (selectedStatus === "past") {
        backendStatus = "past";
      } else if (selectedStatus === "completed") {
        backendStatus = "completed";
      } else if (selectedStatus === "uncompleted") {
        backendStatus = "uncompleted";
      }
      
      // Add cache-busting timestamp to ensure fresh data
      const timestamp = Date.now();
      console.log("🔄 [LOAD ALL VIEWINGS FOR CALENDAR] Cache-busting timestamp:", timestamp);
      
      // Load ALL viewings without pagination (use a very large pageSize or special endpoint)
      // For now, we'll use the same endpoint but with a very large pageSize
      const response = await propertyViewingsService.getUserViewingsForCalendar(
        id,
        backendStatus,
        timestamp,
        1, // Start from page 1
        10000 // Very large page size to get all viewings
      );
      console.log("✅ [LOAD ALL VIEWINGS FOR CALENDAR] All viewings data received:", response);
      
      // Extract all viewings data from response
      let allViewingsData = [];
      
      // Handle paginated response structure
      if (response && typeof response === 'object' && 'data' in response) {
        allViewingsData = response.data || [];
      } else if (Array.isArray(response)) {
        allViewingsData = response;
      }
      
      console.log(`✅ [LOAD ALL VIEWINGS FOR CALENDAR] Loaded ${allViewingsData.length} viewings for calendar`);
      console.log(`📊 [LOAD ALL VIEWINGS FOR CALENDAR] Sample viewing data:`, allViewingsData[0]);
      setAllViewingsForCalendar(allViewingsData);

      // Convert ALL viewings to calendar events
      const events = (allViewingsData || [])
        .map((viewing) => {
          // Safety check for null/undefined viewing
          if (!viewing) {
            console.warn("Skipping null/undefined viewing");
            return null;
          }

          console.log("Processing viewing:", viewing);

          // Handle date and time parsing more safely
          let startDate, endDate;
          try {
            console.log(
              "Parsing dates for viewing:",
              viewing.id,
              "day:",
              viewing.day,
              "time_from:",
              viewing.time_from,
              "time_to:",
              viewing.time_to
            );

            if (viewing.day && viewing.time_from) {
              // Handle different date formats
              let dateString = viewing.day;
              if (viewing.day.includes("/")) {
                // Convert dd/mm/yyyy to yyyy-mm-dd
                const parts = viewing.day.split("/");
                if (parts.length === 3) {
                  dateString = `${parts[2]}-${parts[1]}-${parts[0]}`;
                }
              }
              startDate = new Date(dateString + "T" + viewing.time_from);
              console.log(
                "Created startDate:",
                startDate,
                "from:",
                dateString + "T" + viewing.time_from
              );
            } else if (viewing.start) {
              startDate = new Date(viewing.start);
              console.log("Using viewing.start for startDate:", startDate);
            } else {
              startDate = new Date();
              console.log("Using current date for startDate:", startDate);
            }

            if (viewing.day && viewing.time_to) {
              let dateString = viewing.day;
              if (viewing.day.includes("/")) {
                const parts = viewing.day.split("/");
                if (parts.length === 3) {
                  dateString = `${parts[2]}-${parts[1]}-${parts[0]}`;
                }
              }
              endDate = new Date(dateString + "T" + viewing.time_to);
              console.log(
                "Created endDate:",
                endDate,
                "from:",
                dateString + "T" + viewing.time_to
              );
            } else if (viewing.end) {
              endDate = new Date(viewing.end);
              console.log("Using viewing.end for endDate:", endDate);
            } else {
              endDate = new Date(startDate.getTime() + 60 * 60 * 1000); // 1 hour later
              console.log("Using default endDate (1 hour later):", endDate);
            }
          } catch (error) {
            console.error("Error parsing dates for viewing:", viewing, error);
            startDate = new Date();
            endDate = new Date(startDate.getTime() + 60 * 60 * 1000);
          }

          // Detect if this is a lunch event
          const isLunchEvent = viewing.postcode === 'LUNCH' || 
                              viewing.type === 'lunch' || 
                              viewing.custom_title ||
                              !viewing.property_id;

          return {
            id: viewing.id || `viewing-${Date.now()}-${Math.random()}`,
            title: isLunchEvent 
              ? (viewing.custom_title || viewing.property_title || "Lunch Event")
              : (viewing.property_title || viewing.title || "Unknown Property"),
            start: startDate,
            end: endDate,
            property: isLunchEvent 
              ? (viewing.custom_title || "Lunch Event")
              : (viewing.property_title || viewing.title || "Unknown Property"),
            confirmed: viewing.confirmed || false,
            confirmations_count: viewing.confirmations_count || 0,
            attendees: viewing.attendees || 0,
            rooms_taken: viewing.rooms_taken || 0,
            notes: viewing.notes || "",
            custom_title: viewing.custom_title || null,
            color: isLunchEvent 
              ? "#ff6b6b" 
              : ((viewing.confirmations_count || 0) > 0 ? "#28a745" : "#ffc107"), // Red for lunch, Green if confirmed, Yellow if not
            type: isLunchEvent ? "lunch" : "viewing",
          };
        })
        .filter(Boolean); // Remove any null events

      console.log("✅ [LOAD ALL VIEWINGS FOR CALENDAR] Calendar events created:", events.length);
      setCalendarEvents(events);
    } catch (err) {
      console.error("❌ [LOAD ALL VIEWINGS FOR CALENDAR] Failed to load all viewings for calendar:", err);
      // Set empty arrays to ensure page loads without data
      setAllViewingsForCalendar([]);
      setCalendarEvents([]);
    }
  }, [id, selectedStatus]);

  // Load paginated viewings for table
  const loadViewings = useCallback(async () => {
    try {
      setLoading(true);
      console.log(
        "🔄 [LOAD VIEWINGS] Loading paginated viewings for table for user:",
        id,
        "status:",
        selectedStatus,
        "page:",
        currentPage,
        "pageSize:",
        pageSize
      );
      
      // Map frontend status to backend status (Laravel-compatible)
      let backendStatus = selectedStatus;
      if (selectedStatus === "all") {
        backendStatus = "all";
      } else if (selectedStatus === "upcoming") {
        backendStatus = "upcoming";
      } else if (selectedStatus === "past") {
        backendStatus = "past";
      } else if (selectedStatus === "completed") {
        backendStatus = "completed";
      } else if (selectedStatus === "uncompleted") {
        backendStatus = "uncompleted";
      }
      
      // Add cache-busting timestamp to ensure fresh data
      const timestamp = Date.now();
      console.log("🔄 [LOAD VIEWINGS] Cache-busting timestamp:", timestamp);
      
      const response = await propertyViewingsService.getUserViewingsForCalendar(
        id,
        backendStatus,
        timestamp, // Pass timestamp for cache busting
        currentPage,
        pageSize
      );
      console.log("✅ [LOAD VIEWINGS] Paginated viewings data received:", response);
      
      // Extract viewings data from response
      let viewingsData = [];
      let totalCount = 0;
      
      // Handle paginated response structure
      if (response && typeof response === 'object' && 'data' in response) {
        // Paginated response structure
        viewingsData = response.data || [];
        totalCount = response.total || 0;
        setViewings(viewingsData);
        setTotalRecords(totalCount);
      } else if (Array.isArray(response)) {
        // Fallback: array response (backward compatibility)
        viewingsData = response;
        totalCount = response.length;
        setViewings(viewingsData);
        setTotalRecords(totalCount);
      } else {
        viewingsData = [];
        totalCount = 0;
        setViewings([]);
        setTotalRecords(0);
      }
    } catch (err) {
      console.error("❌ [LOAD VIEWINGS] Failed to load paginated viewings:", err);
      // Set empty arrays to ensure page loads without data
      setViewings([]);
      setTotalRecords(0);
      setError("Failed to load viewings - showing empty state");
    } finally {
      setLoading(false);
    }
  }, [id, selectedStatus, currentPage, pageSize]);

  const loadCalendarData = useCallback(async () => {
    try {
      const calendarData = await propertyViewingsService.getUserCalendar(
        id
      );
      if (calendarData && calendarData.events) {
        setCalendarEvents(calendarData.events);
      }
    } catch (err) {
      console.error("Failed to load calendar data:", err);
      // Set empty calendar events to ensure page loads
      setCalendarEvents([]);
    }
  }, [id]);

  useEffect(() => {
    const initializeData = async () => {
      try {
        // Load all data in parallel, but don't fail if one fails
        await Promise.allSettled([
          loadUserData(),
          loadViewingStats(),
          loadViewings(), // Paginated viewings for table
          loadAllViewingsForCalendar(), // ALL viewings for calendar (no pagination)
        ]);
      } catch (err) {
        console.error("Error during initialization:", err);
        // Page will still load with fallback data
      }
    };

    initializeData();
  }, [loadUserData, loadViewingStats, loadViewings, loadAllViewingsForCalendar]);

  // Initialize tooltips with proper cleanup
  useEffect(() => {
    const tooltipElements = [];
    const eventListeners = [];

    const initTooltips = () => {
      const elements = document.querySelectorAll('[data-toggle="tooltip"]');
      
      elements.forEach((element) => {
        const title = element.getAttribute("title");
        if (title) {
          tooltipElements.push(element);
          
          const mouseenterHandler = (e) => {
            const tooltip = document.createElement("div");
            tooltip.className = "tooltip-inner";
            tooltip.textContent = title;
            tooltip.style.cssText = `
              position: absolute;
              background: #000;
              color: #fff;
              padding: 5px 10px;
              border-radius: 4px;
              font-size: 12px;
              z-index: 1000;
              white-space: nowrap;
            `;

            const rect = element.getBoundingClientRect();
            tooltip.style.left =
              rect.left + rect.width / 2 - tooltip.offsetWidth / 2 + "px";
            tooltip.style.top = rect.top - tooltip.offsetHeight - 5 + "px";

            document.body.appendChild(tooltip);
            element._tooltip = tooltip;
          };

          const mouseleaveHandler = () => {
            if (element._tooltip) {
              element._tooltip.remove();
              element._tooltip = null;
            }
          };

          element.addEventListener("mouseenter", mouseenterHandler);
          element.addEventListener("mouseleave", mouseleaveHandler);
          
          // Store references for cleanup
          eventListeners.push({
            element,
            mouseenter: mouseenterHandler,
            mouseleave: mouseleaveHandler
          });
        }
      });
    };

    // Initialize tooltips after component mounts and when viewings change
    const timeoutId = setTimeout(initTooltips, 100);

    // Cleanup function
    return () => {
      clearTimeout(timeoutId);
      
      // Remove all event listeners
      eventListeners.forEach(({ element, mouseenter, mouseleave }) => {
        element.removeEventListener("mouseenter", mouseenter);
        element.removeEventListener("mouseleave", mouseleave);
        
        // Clean up any existing tooltips
        if (element._tooltip) {
          element._tooltip.remove();
          element._tooltip = null;
        }
      });
      
      // Clear arrays
      tooltipElements.length = 0;
      eventListeners.length = 0;
    };
  }, [viewings]);

  const handleStatusFilter = (status) => {
    setSelectedStatus(status);
    setCurrentPage(1); // Reset to first page when filter changes
  };
  
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    // Scroll to top of table when page changes
    const tableElement = document.getElementById("datatable-property-viewings-of-user");
    if (tableElement) {
      tableElement.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };
  
  const handlePageSizeChange = (newPageSize) => {
    setPageSize(parseInt(newPageSize));
    setCurrentPage(1); // Reset to first page when page size changes
  };

  const handleRefreshStats = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadViewings(), // Paginated viewings for table
        loadAllViewingsForCalendar(), // ALL viewings for calendar
        loadViewingStats()
      ]);
    } catch (err) {
      console.error("Failed to refresh stats:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddViewing = async () => {
    console.log('🔍 [CALENDAR] handleAddViewing called, current user:', user);
    
    try {
      // Force refresh user data and wait for it
      console.log('🔄 [CALENDAR] Refreshing user data before opening modal...');
      const freshUserData = await propertyViewingsService.getUser(id);
      console.log('✅ [CALENDAR] Fresh user data received:', freshUserData);
      
      if (freshUserData) {
        setUser(freshUserData);
        setModalUserData(freshUserData);
        console.log('🔍 [CALENDAR] Opening modal with user:', freshUserData);
        setShowAddModal(true);
      } else {
        console.log('🔍 [CALENDAR] Opening modal without user data');
        setShowAddModal(true);
      }
    } catch (err) {
      console.error('❌ [CALENDAR] Error refreshing user data:', err);
      setShowAddModal(true);
    }
  };

  const handleAddLunch = async () => {
    console.log('🔍 [CALENDAR] handleAddLunch called, current user:', user);
    
    try {
      // Always refresh user data to ensure it's current
      console.log('🔄 [CALENDAR] Refreshing user data before opening lunch modal...');
      const freshUserData = await propertyViewingsService.getUser(id);
      console.log('✅ [CALENDAR] Fresh user data for lunch:', freshUserData);
      
      if (freshUserData) {
        setUser(freshUserData);
        setModalUserData(freshUserData);
        console.log('🔍 [CALENDAR] Opening lunch modal with user:', freshUserData);
      } else {
        // Use existing user data if fresh data not available
        console.log('🔍 [CALENDAR] Using existing user data for lunch modal');
        setModalUserData(user);
      }
      
      setShowLunchModal(true);
    } catch (err) {
      console.error('❌ [CALENDAR] Error refreshing user data for lunch:', err);
      // Still open modal with existing user data
      setModalUserData(user);
      setShowLunchModal(true);
    }
  };

  const handleEditViewing = (viewing) => {
    console.log('🔍 [EDIT] Viewing to edit:', viewing);
    
    // Enhanced lunch event detection
    const isLunchEvent = viewing.property_title === 'Lunch Event' || 
                        viewing.postcode === 'LUNCH' || 
                        viewing.type === 'lunch' ||
                        viewing.custom_title ||
                        !viewing.property_id ||
                        (viewing.notes && viewing.notes.includes('Lunch'));
    
    console.log('🔍 [EDIT] Is lunch event?', isLunchEvent);
    console.log('🔍 [EDIT] Detection criteria:', {
      property_title: viewing.property_title,
      postcode: viewing.postcode,
      type: viewing.type,
      custom_title: viewing.custom_title,
      property_id: viewing.property_id,
      notes: viewing.notes
    });
    
    if (isLunchEvent) {
      console.log('🍽️ [EDIT] Opening lunch edit modal');
      
      // Fetch fresh lunch data from backend before opening modal
      const fetchFreshLunchData = async () => {
        try {
          console.log('🔄 [EDIT] Fetching fresh lunch data for ID:', viewing.id);
          
          // Get fresh lunch data from backend
          const freshLunchData = await propertyViewingsService.getViewingById(viewing.id);
          console.log('✅ [EDIT] Fresh lunch data received:', freshLunchData);
          
          if (freshLunchData && freshLunchData.data) {
            setSelectedLunch(freshLunchData.data);
          } else {
            // Fallback to existing data if API fails
            console.warn('⚠️ [EDIT] Fresh data not available, using cached data');
            setSelectedLunch(viewing);
          }
        } catch (error) {
          console.error('❌ [EDIT] Error fetching fresh lunch data:', error);
          // Fallback to existing data
          setSelectedLunch(viewing);
        }
        
        setShowLunchModal(true);
      };
      
      fetchFreshLunchData();
    } else {
      console.log('📅 [EDIT] Opening property viewing edit modal');
      
      // Fetch fresh viewing data with confirmations before opening modal
      const fetchFreshViewingData = async () => {
        try {
          console.log('🔄 [EDIT] Fetching fresh viewing data for ID:', viewing.id);
          
          // Get fresh viewing data from backend (includes confirmations)
          const freshViewingData = await propertyViewingsService.getViewingById(viewing.id);
          console.log('✅ [EDIT] Fresh viewing data received:', freshViewingData);
          
          if (freshViewingData && freshViewingData.data) {
            setSelectedViewing(freshViewingData.data);
          } else {
            // Fallback to existing data if API fails
            console.warn('⚠️ [EDIT] Fresh data not available, using cached data');
            setSelectedViewing(viewing);
          }
        } catch (error) {
          console.error('❌ [EDIT] Error fetching fresh viewing data:', error);
          // Fallback to existing data
          setSelectedViewing(viewing);
        }
        
        setShowEditModal(true);
      };
      
      fetchFreshViewingData();
    }
  };

  const handleDeleteViewing = async (viewingId) => {
    if (window.confirm("Are you sure you want to delete this viewing and all related records?")) {
      try {
        setSubmitting(true);
        console.log("🔍 [FRONTEND] Deleting viewing:", viewingId);
        
        const response = await propertyViewingsService.deleteViewing(viewingId);
        console.log("✅ [FRONTEND] Viewing deleted successfully:", response);
        
        toast.success("Viewing deleted successfully!");
        
        await Promise.all([
          loadViewings(), // Paginated viewings for table
          loadAllViewingsForCalendar(), // ALL viewings for calendar
          loadViewingStats()
        ]);
        setShowEditModal(false);
        setSelectedViewing(null);
      } catch (err) {
        console.error("❌ [FRONTEND] Failed to delete viewing:", err);
        const errorMessage = err.response?.data?.message || err.message || "Failed to delete viewing";
        toast.error(errorMessage);
      } finally {
        setSubmitting(false);
      }
    }
  };

  const handleSubmitViewing = async (formData) => {
    try {
      setSubmitting(true);
      console.log("🔍 [FRONTEND] Submitting viewing data:", formData);
      
      if (selectedViewing?.id) {
        // Edit existing viewing
        const response = await propertyViewingsService.updateViewing(
          selectedViewing.id,
          formData
        );
        console.log("✅ [FRONTEND] Viewing updated successfully:", response);
        toast.success("Viewing updated successfully!");
      } else {
        // Create new viewing
        const response = await propertyViewingsService.createViewing(formData);
        console.log("✅ [FRONTEND] Viewing created successfully:", response);
        toast.success("Viewing created successfully!");
      }
      
      await Promise.all([
        loadViewings(), // Paginated viewings for table
        loadAllViewingsForCalendar(), // ALL viewings for calendar
        loadViewingStats()
      ]);
      setShowAddModal(false);
      setShowEditModal(false);
      setSelectedViewing(null);
    } catch (err) {
      console.error("❌ [FRONTEND] Failed to save viewing:", err);
      const errorMessage = err.response?.data?.message || err.message || "Failed to save viewing";
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitLunch = async (formData) => {
    try {
      setSubmitting(true);
      console.log("🔍 [FRONTEND] Submitting lunch data:", formData);
      
      if (selectedLunch?.id) {
        // Edit existing lunch - DELETE OLD AND CREATE NEW (like Laravel)
        console.log("🔄 [EDIT LUNCH] Deleting old lunch event:", selectedLunch.id);
        
        try {
          // Delete old lunch event
          await propertyViewingsService.deleteViewing(selectedLunch.id);
          console.log("✅ [EDIT LUNCH] Old lunch event deleted");
        } catch (deleteError) {
          console.warn("⚠️ [EDIT LUNCH] Failed to delete old lunch event:", deleteError);
          // Continue with update anyway
        }
        
        // Create new lunch event with updated data
        const response = await propertyViewingsService.addLunch(formData);
        console.log("✅ [FRONTEND] Lunch recreated successfully:", response);
        toast.success("Lunch event updated successfully!");
      } else {
        // Create new lunch
        const response = await propertyViewingsService.addLunch(formData);
        console.log("✅ [FRONTEND] Lunch added successfully:", response);
        toast.success("Lunch event added successfully!");
      }
      
      setShowLunchModal(false);
      setSelectedLunch(null);
      
      // Add delay before refresh to ensure database is updated
      console.log('🔄 [LUNCH UPDATE] Waiting 500ms before refresh to ensure database consistency');
      setTimeout(async () => {
        // Refresh calendar data
        await Promise.all([
          loadViewings(), // Paginated viewings for table
          loadAllViewingsForCalendar(), // ALL viewings for calendar
          loadViewingStats()
        ]);
        console.log('✅ [LUNCH UPDATE] Calendar data refreshed after lunch update');
      }, 500);
    } catch (err) {
      console.error("❌ [FRONTEND] Failed to save lunch:", err);
      const errorMessage = err.response?.data?.message || err.message || "Failed to save lunch event";
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopyToNextWeek = async () => {
    if (selectedViewing?.id) {
      try {
        setSubmitting(true);
        console.log("🔍 [FRONTEND] Copying viewing to next week:", selectedViewing.id);
        
        const response =         await propertyViewingsService.copyViewingToNextWeek(
          selectedViewing.id,
          "addWeek"
        );
        
        console.log("✅ [FRONTEND] Viewing copied successfully:", response);
        toast.success("Viewing copied to next week successfully!");
        
        await Promise.all([
          loadViewings(), // Paginated viewings for table
          loadAllViewingsForCalendar(), // ALL viewings for calendar
          loadViewingStats()
        ]);
        setShowEditModal(false);
        setSelectedViewing(null);
      } catch (err) {
        console.error("❌ [FRONTEND] Failed to copy viewing:", err);
        const errorMessage = err.response?.data?.message || err.message || "Failed to copy viewing";
        toast.error(errorMessage);
      } finally {
        setSubmitting(false);
      }
    }
  };

  const handleCalendarEventClick = (event) => {
    console.log('🔍 [CALENDAR CLICK] Event clicked:', event);
    
    // Handle both viewing and lunch events
    if (event.type === "viewing" || event.type === "lunch") {
      // Search in allViewingsForCalendar first (has all viewings), then fallback to paginated viewings
      const viewing = allViewingsForCalendar.find((v) => v.id === event.id) || 
                      viewings.find((v) => v.id === event.id);
      console.log('🔍 [CALENDAR CLICK] Found viewing data:', viewing);
      
      if (viewing) {
        console.log('🔍 [CALENDAR CLICK] Calling handleEditViewing with:', viewing);
        handleEditViewing(viewing);
      } else {
        console.warn('🔍 [CALENDAR CLICK] Viewing not found in viewings array for ID:', event.id);
        // Try to create viewing data from event
        const eventAsViewing = {
          id: event.id,
          property_title: event.title,
          postcode: event.type === "lunch" ? "LUNCH" : "",
          day: event.start ? new Date(event.start).toLocaleDateString("en-GB") : "",
          time_from: event.start ? new Date(event.start).toTimeString().slice(0, 5) : "00:00",
          time_to: event.end ? new Date(event.end).toTimeString().slice(0, 5) : "00:00",
          confirmed: event.confirmed || false,
          attendees: event.attendees || 0,
          rooms_taken: event.rooms_taken || 0,
          notes: event.notes || "",
          custom_title: event.custom_title || event.title,
          type: event.type
        };
        console.log('🔍 [CALENDAR CLICK] Created viewing from event:', eventAsViewing);
        handleEditViewing(eventAsViewing);
      }
    } else {
      console.warn('🔍 [CALENDAR CLICK] Unknown event type:', event.type);
    }
  };

  const handleCalendarDateChange = async (eventId, newDate) => {
    try {
      // Search in allViewingsForCalendar first (has all viewings), then fallback to paginated viewings
      const viewing = allViewingsForCalendar.find((v) => v.id === eventId) || 
                      viewings.find((v) => v.id === eventId);
      if (viewing) {
        const updatedViewing = {
          ...viewing,
          day: newDate.toISOString().split("T")[0],
          time_from: newDate.toTimeString().slice(0, 5),
          time_to: new Date(
            newDate.getTime() + (viewing.time_to - viewing.time_from)
          )
            .toTimeString()
            .slice(0, 5),
        };
        await propertyViewingsService.updateViewing(eventId, updatedViewing);
        // Refresh both paginated viewings and all viewings for calendar
        await Promise.all([
          loadViewings(), // Paginated viewings for table
          loadAllViewingsForCalendar() // ALL viewings for calendar
        ]);
      }
    } catch (err) {
      console.error("Failed to update viewing date:", err);
    }
  };

  const renderStatusOverview = () => {
    const statusBoxes = [
      {
        id: "all",
        label: "All",
        count: viewingStats.all,
        color: "#5bc0de",
        statusId: "999",
      },
      {
        id: "upcoming",
        label: "Upcoming",
        count: viewingStats.upcoming,
        color: "#337ab7",
        statusId: "0",
      },
      {
        id: "past",
        label: "Past",
        count: viewingStats.past,
        color: "#5cb85c",
        statusId: "1",
      },
      {
        id: "completed",
        label: "Completed",
        count: viewingStats.completed,
        color: "#cc6600",
        statusId: "2",
      },
      {
        id: "uncompleted",
        label: "Uncompleted",
        count: viewingStats.uncompleted,
        color: "midnightblue",
        statusId: "3",
      },
    ];

    return (
      <div className="row">
        <div className="col-md-12">
          <div className="panel-title title-standard">
            <h3>Status overview</h3>
          </div>
          <div className="panel-group mb-20 border-down">
            {statusBoxes.map((status) => (
              <div key={status.id} className="col-md-2">
                <div
                  className={`activity-box js-property-viewing-overview-box ${
                    selectedStatus === status.id ? "active" : ""
                  }`}
                  style={{ backgroundColor: status.color }}
                  onClick={() => handleStatusFilter(status.id)}
                  data-status_id={status.statusId}
                >
                  <p className="activity-value">{status.count}</p>
                  <p className="activity-title" data-to_display={status.label}>
                    {status.label}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderViewingsTable = () => {
    return (
      <div className="panel panel-default panel-standard">
        <div
          style={{ margin: "0" }}
          id="property-status-span"
          className="panel-heading panel-title"
        >
          <h3>List</h3>
        </div>
        <div className="panel-body">
          {viewings.length > 0 || totalRecords > 0 ? (
            <div className="dataTable_wrapper">
              {/* Pagination Controls - Top */}
              <div className="row" style={{ marginBottom: "15px" }}>
                <div className="col-sm-6">
                  <div className="dataTables_length">
                    <label>
                      Show{" "}
                      <select
                        value={pageSize}
                        onChange={(e) => handlePageSizeChange(e.target.value)}
                        className="form-control input-sm"
                        style={{ display: "inline-block", width: "auto", margin: "0 5px" }}
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={30}>30</option>
                        <option value={50}>50</option>
                        <option value={75}>75</option>
                        <option value={100}>100</option>
                      </select>{" "}
                      entries
                    </label>
                  </div>
                </div>
                <div className="col-sm-6 text-right">
                  <div className="dataTables_info">
                    Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalRecords)} of {totalRecords} entries
                  </div>
                </div>
              </div>
              
              <table
                id="datatable-property-viewings-of-user"
                data-user={user?.id}
                className="table table-hover dt-responsive"
              >
                <thead>
                  <tr>
                    <th>Property</th>
                    <th>ID</th>
                    <th>Postcode</th>
                    <th>Day</th>
                    <th>From</th>
                    <th>To</th>
                    <th>Confirmed</th>
                    <th>Attendees</th>
                    <th>Taken</th>
                    <th>Calendar</th>
                    <th className="action-head">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {viewings
                    .map((viewing) => {
                      // Safety check for null/undefined viewing
                      if (!viewing) {
                        console.warn(
                          "Skipping null/undefined viewing in table"
                        );
                        return null;
                      }

                      return (
                        <tr
                          key={
                            viewing.id ||
                            `viewing-${Date.now()}-${Math.random()}`
                          }
                        >
                          <td>
                            {(() => {
                              const propertyId = viewing.property?.id || viewing.property_id;
                              if (propertyId) {
                                return (
                                  <a
                                    href={`/properties/${propertyId}`}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      navigate(`/properties/${propertyId}`);
                                    }}
                                    style={{ color: '#337ab7', textDecoration: 'none', cursor: 'pointer' }}
                                    onMouseEnter={(e) => {
                                      e.target.style.textDecoration = 'underline';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.target.style.textDecoration = 'none';
                                    }}
                                  >
                                    {viewing.property_title || "Unknown Property"}
                                  </a>
                                );
                              }
                              return viewing.property_title || "Unknown Property";
                            })()}
                          </td>
                          <td>{viewing.id || ""}</td>
                          <td>{viewing.postcode || ""}</td>
                          <td>{viewing.day || ""}</td>
                          <td>{viewing.time_from || ""}</td>
                          <td>{viewing.time_to || ""}</td>
                          <td>
                            <span
                              className={`status-badge ${
                                (viewing.confirmations_count || 0) > 0 ? "confirmed" : "pending"
                              }`}
                              style={{ cursor: "pointer" }}
                              onClick={() => navigate(`/property_viewings/${viewing.id}/edit`)}
                              title="Click to edit viewing"
                            >
                              {viewing.confirmations_count || 0}
                            </span>
                          </td>
                          <td>{viewing.attendees || 0}</td>
                          <td>{viewing.rooms_taken || 0}</td>
                          <td>
                            <button
                              className="btn btn-sm btn-primary"
                              disabled={!viewing.property_id && !viewing.property?.id}
                              onClick={() => {
                                const propertyId = viewing.property?.id || viewing.property_id;
                                if (propertyId) {
                                  navigate(`/property_viewings/property/${propertyId}`);
                                }
                              }}
                            >
                              Property
                            </button>
                          </td>
                          <td>
                            <div className="btn-group">
                              <button
                                className="btn btn-sm btn-secondary"
                                onClick={() => navigate(`/property_viewings/${viewing.id}/edit`)}
                              >
                                Edit
                              </button>
                              <button
                                className="btn btn-sm btn-danger"
                                onClick={() => handleDeleteViewing(viewing.id)}
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                    .filter(Boolean)}
                </tbody>
              </table>
              
              {/* Pagination Controls - Bottom */}
              {totalRecords > pageSize && (
                <div className="row" style={{ marginTop: "15px" }}>
                  <div className="col-sm-12">
                    <div className="dataTables_paginate paging_simple_numbers">
                      <ul className="pagination" style={{ margin: "0", justifyContent: "center" }}>
                        <li className={`paginate_button previous ${currentPage === 1 ? "disabled" : ""}`}>
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              if (currentPage > 1) {
                                handlePageChange(currentPage - 1);
                              }
                            }}
                            style={{ cursor: currentPage === 1 ? "not-allowed" : "pointer" }}
                          >
                            Previous
                          </a>
                        </li>
                        {Array.from({ length: Math.ceil(totalRecords / pageSize) }, (_, i) => i + 1)
                          .filter((page) => {
                            // Show first page, last page, current page, and pages around current
                            return (
                              page === 1 ||
                              page === Math.ceil(totalRecords / pageSize) ||
                              (page >= currentPage - 2 && page <= currentPage + 2)
                            );
                          })
                          .map((page, index, array) => {
                            // Add ellipsis if there's a gap
                            const showEllipsisBefore = index > 0 && page - array[index - 1] > 1;
                            return (
                              <React.Fragment key={page}>
                                {showEllipsisBefore && (
                                  <li className="paginate_button disabled">
                                    <a href="#">...</a>
                                  </li>
                                )}
                                <li className={`paginate_button ${currentPage === page ? "active" : ""}`}>
                                  <a
                                    href="#"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      handlePageChange(page);
                                    }}
                                  >
                                    {page}
                                  </a>
                                </li>
                              </React.Fragment>
                            );
                          })}
                        <li className={`paginate_button next ${currentPage >= Math.ceil(totalRecords / pageSize) ? "disabled" : ""}`}>
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              if (currentPage < Math.ceil(totalRecords / pageSize)) {
                                handlePageChange(currentPage + 1);
                              }
                            }}
                            style={{ cursor: currentPage >= Math.ceil(totalRecords / pageSize) ? "not-allowed" : "pointer" }}
                          >
                            Next
                          </a>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center">
              <p className="text-muted">
                {loading
                  ? "Loading viewings..."
                  : "No viewings scheduled for this user."}
              </p>
              {!loading && (
                <button
                  className="btn btn-primary"
                  onClick={handleRefreshStats}
                >
                  ↻ Refresh Data
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

      // Safety check for id
    if (!id) {
    return (
      <div className="container-fluid">
        <div className="alert alert-danger">
          <strong>Error:</strong> No user ID provided. Please check the URL.
        </div>
      </div>
    );
  }

  // Show loading state only during initial load
  if (loading && !user && !viewings.length) {
    return <Loader />;
  }

  return (
    <div className="container-fluid">
      {/* Non-blocking error display */}
      {error && (
        <div
          className="alert alert-warning alert-dismissible fade show"
          role="alert"
        >
          <strong>Warning:</strong> {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError(null)}
            aria-label="Close"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
      )}

      <div className="breadcrumb-row">
        <span
          className="breadcrumb-main"
          style={{ fontWeight: 400, fontSize: "14px", color: "black", cursor: 'pointer' }}
          onClick={() => navigate('/dashboard')}
        >
          Dashboard
        </span>
        <span
          className="breadcrumb-main"
          style={{ fontWeight: 400, fontSize: "14px", color: "black", cursor: 'pointer' }}
          onClick={() => navigate('/property_viewings')}
        >
          / Property Viewings
        </span>
        <span
          className="breadcrumb-main"
          style={{ fontWeight: 400, fontSize: "14px", color: "black" }}
        >
          / {user?.fullName || `User ${id}`}
        </span>
        <div
          style={{ background: "#30C693", fontWeight: 400, fontSize: "14px" }}
        >
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-properties">Calendar</span>
        </div>
      </div>

      <div
        id="js-properties-typeahead"
        className="row js-property-viewing-of-user"
      >
        <div id="js-user-id" data-user={user?.id}></div>

        <div className="col-md-12">
          <div className="panel panel-default panel-standard">
            <div
              style={{ margin: "0" }}
              id="property-status-span"
              className="panel-heading panel-title"
            >
              <h3 className="mr-auto">
                {user?.fullName || `User ${id}`}' property viewings
              </h3>
              <div className="ml-auto">
                <button
                  style={{ background: "#1B2046", color: "white" }}
                  id="js-add-property-viewing-of-user"
                  className="btn btn-secondary ml-2"
                  onClick={handleAddViewing}
                >
                  Add Viewing
                </button>
                <button
                  style={{ background: "#1B2046", color: "white" }}
                  id="js-add-lunch"
                  className="btn btn-secondary ml-2"
                  onClick={handleAddLunch}
                >
                  Add Lunch
                </button>
              </div>
            </div>
            <div className="panel-body">
              <div id="property-viewings-calendar-user">
                {console.log(
                  "📅 [CALENDAR] Events being passed to calendar component:",
                  calendarEvents.length,
                  "events"
                )}
                {console.log(
                  "📅 [CALENDAR] Viewings data for calendar:",
                  allViewingsForCalendar.length,
                  "viewings"
                )}
                {console.log(
                  "📅 [CALENDAR] Sample viewing:",
                  allViewingsForCalendar[0]
                )}
                <PropertyViewingsCalendar
                  userId={id}
                  viewings={allViewingsForCalendar} // Use ALL viewings for calendar, not paginated
                  events={calendarEvents}
                  onEventClick={handleCalendarEventClick}
                  onDateChange={handleCalendarDateChange}
                  viewMode={calendarViewMode}
                  onViewModeChange={setCalendarViewMode}
                />
              </div>
            </div>
          </div>

          {renderStatusOverview()}
          {renderViewingsTable()}
        </div>
      </div>

      {/* Add Property Viewing Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Add property viewing"
        submitText="Create"
        onSubmit={handleSubmitViewing}
        dataTableId="#datatable-property-viewings-of-user"
      >
        <PropertyViewingForm 
          key="add-viewing-form" 
          user={modalUserData || user} 
          onSubmit={handleSubmitViewing} 
        />
      </Modal>

      {/* Edit Property Viewing Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedViewing(null);
        }}
        title="Edit property viewing"
        submitText="Save"
        onSubmit={handleSubmitViewing}
        showDelete={true}
        onDelete={() => handleDeleteViewing(selectedViewing?.id)}
        showCopy={true}
        onCopy={handleCopyToNextWeek}
        dataTableId="#datatable-property-viewings-of-user"
      >
        <PropertyViewingForm
          key={`edit-viewing-${selectedViewing?.id || 'new'}`}
          viewing={selectedViewing}
          user={user}
          onSubmit={handleSubmitViewing}
          isEdit={true}
        />
      </Modal>

      {/* Add Lunch Modal */}
      <Modal
        isOpen={showLunchModal}
        onClose={() => {
          setShowLunchModal(false);
          setSelectedLunch(null);
        }}
        title={selectedLunch ? "Edit lunch" : "Add lunch"}
        submitText={selectedLunch ? "Save" : "Create"}
        onSubmit={handleSubmitLunch}
      >
        <LunchForm 
          user={modalUserData || user} 
          onSubmit={handleSubmitLunch} 
          lunch={selectedLunch || null}
          isEdit={!!selectedLunch}
        />
      </Modal>
    </div>
  );
};

export default PropertyViewingsUserCalendar;
